import { useState, useCallback, useMemo } from 'react';
import { cloneDeep, get } from 'lodash';
import flatten from 'flat';

export enum TabStatus {
  Wait = 'wait',
  Process = 'process',
  Finish = 'finish',
  Error = 'error',
}

type Tabs = {
  title: string;
  status: TabStatus;
  isDraftValid?: boolean;
  draftValidationFunc?: Function;
}[];

export const getTabStatus = (formik: any) => {
  const flattenErrors: any = flatten(formik.errors);

  const hasVisibleErrors = Object.keys(flattenErrors).some((key) =>
    get(formik.touched, key, false),
  );

  if (hasVisibleErrors) return TabStatus.Error;

  if (formik.isValid && Object.values(formik.touched).some(Boolean)) {
    return TabStatus.Finish;
  }

  return TabStatus.Wait;
};

export default function useFormTabs(
  defaultTabs: Tabs,
  defaultActiveTab: number,
) {
  const [tabs, setTabs] = useState(defaultTabs);
  const [activeTab, setActiveTab] = useState(defaultActiveTab);

  const handleFormChange = useCallback((formik: any, tabKey: any) => {
    setTabs((oldTabs) => {
      const newStatus = getTabStatus(formik);
      const newDraftStatus = oldTabs[tabKey].draftValidationFunc?.(formik);

      if (
        newStatus === oldTabs[tabKey].status &&
        newDraftStatus === oldTabs[tabKey].isDraftValid
      )
        return oldTabs;

      const newTabs = cloneDeep(oldTabs);
      newTabs[tabKey].status = newStatus;
      newTabs[tabKey].isDraftValid = newDraftStatus;
      return newTabs;
    });
  }, []);

  const onFormChange = useMemo(() => {
    return tabs.map(
      (tab, index) => (formik: any) => handleFormChange(formik, index),
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetTabs = useCallback((newTabs: Tabs) => {
    setTabs(newTabs);
  }, []);

  return [tabs, onFormChange, resetTabs, activeTab, setActiveTab] as const;
}
